// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
import type {World} from '@core/sim/World'
const N=40,CS=Array.from({length:N+1},(_,i)=>Math.cos(i*Math.PI/N)),SN=Array.from({length:N+1},(_,i)=>Math.sin(i*Math.PI/N))
function segment2(x:number,y:number,z:number,ax:number,ay:number,az:number,bx:number,by:number,bz:number):number{
 const dx=bx-ax,dy=by-ay,dz=bz-az,t=Math.max(0,Math.min(1,((x-ax)*dx+(y-ay)*dy+(z-az)*dz)/(dx*dx+dy*dy+dz*dz)))
 return (x-ax-dx*t)**2+(y-ay-dy*t)**2+(z-az-dz*t)**2
}
function gateClear(world:World,ax:number,ay:number,ux:number,uy:number,length:number,angle:number,alpha:number):boolean{
 const c=Math.cos(angle),s=Math.sin(angle)
 for(const b of world.balls){if(!b.active)continue
  const dx=b.prevX+(b.x-b.prevX)*alpha-world.frameX-ax,dy=b.prevY+(b.y-b.prevY)*alpha-world.frameY-ay,bz=b.prevZ+(b.z-b.prevZ)*alpha-.030,u=dx*ux+dy*uy,v=-dx*uy+dy*ux
  if(u<-.04||u>length+.04||Math.abs(v)>.04||bz<-.07||bz>length+.04)continue
  const x=c*u+s*bz,z=-s*u+c*bz,ex=x-Math.max(-.0021,Math.min(length,x)),ey=v-Math.max(-.0105,Math.min(.0048,v)),ez=z-Math.max(-.02794,Math.min(.0021,z))
  if(ex*ex+ey*ey+ez*ez<(b.radius+.00004)**2)return false
 }
 return true
}
/** Visible flexible hardware yields at the sphere's leading surface. Electrical switch timing stays in Core. */
export function gateContactPose(world:World|undefined,ax:number,ay:number,bx:number,by:number,base:number,alpha:number):number{
 if(!world)return base
 const length=Math.hypot(bx-ax,by-ay),ux=(bx-ax)/length,uy=(by-ay)/length
 if(gateClear(world,ax,ay,ux,uy,length,base,alpha))return base
 let lo=base,hi=Math.PI
 for(let i=0;i<12;i++){const a=(lo+hi)/2;if(gateClear(world,ax,ay,ux,uy,length,a,alpha))hi=a;else lo=a}
 return hi
}
function rolloverClear(world:World,cx:number,cy:number,bearing:number,span:number,depth:number,angle:number,alpha:number):boolean{
 const c=Math.cos(bearing),s=Math.sin(bearing),sa=Math.sin(angle),ca=Math.cos(angle)
 for(const b of world.balls){if(!b.active)continue
  const dx=b.prevX+(b.x-b.prevX)*alpha-world.frameX-cx,dy=b.prevY+(b.y-b.prevY)*alpha-world.frameY-cy,x=c*dx+s*dy,y=-s*dx+c*dy,z=b.prevZ+(b.z-b.prevZ)*alpha
  if(Math.abs(x)>span+b.radius+.002||Math.abs(y)>.012+b.radius||z>b.radius+.012||z<0)continue
  let px=-span,py=0,pz=-depth;const limit=(b.radius+.0011+.00004)**2
  for(let i=1;i<=N;i++){
   const h=.010*SN[i]!,qx=-span*CS[i]!,qy=-h*sa,qz=h*ca-depth
   if(segment2(x,y,z,px,py,pz,qx,qy,qz)<limit)return false
   px=qx;py=qy;pz=qz
  }
 }
 return true
}
export function rolloverContactPose(world:World|undefined,cx:number,cy:number,bearing:number,span:number,depth:number,base:number,alpha:number):number{
 if(!world||depth<=0||rolloverClear(world,cx,cy,bearing,span,depth,base,alpha))return base
 let lo=base,hi=1.43
 for(let i=0;i<12;i++){const a=(lo+hi)/2;if(rolloverClear(world,cx,cy,bearing,span,depth,a,alpha))hi=a;else lo=a}
 return hi
}
